// @ts-check

import * as Sentry from "@sentry/react";
import { z } from 'zod';
import { useQuery, useMutation, useInfiniteQuery, queryOptions, infiniteQueryOptions, mutationOptions, useQueryClient } from "@tanstack/react-query"
import { ChatContext } from './index.jsx'
import { AgentMode, AI_USER, AGENT_USER, DEEP_RESEARCH_USER } from './utils.js'
import { useEffect, useId, useState } from "react"
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai"
import { streamLLMInference, RateLimitError, getRateLimitData, checkClientRateLimit } from '@minohealth/common/services/LLMApiService';
import { store } from "@/lib/store.js";
import { ChatMessagePartSchema } from "@/components/chat-bubble/utils.js";
import { toast } from "sonner";
import useSharedEffect from "@/hooks/useSharedEffect.js";
import { useSelector } from "react-redux";
import { useChatAuthContext } from "@/components/layout/chat-layout/index.jsx";

// localStorage key for guest chat persistence
const GUEST_CHAT_STORAGE_KEY = 'moremi_guest_chat_data';
const CHAT_EXPIRY_HOURS = 24;

/**
 * Save guest chat messages to localStorage
 * @param {Array<ChatMessage>} messages - Array of chat messages
 */
function saveGuestChatToStorage(messages) {
  try {
    const chatData = {
      messages,
      timestamp: Date.now(),
      version: 1
    };
    localStorage.setItem(GUEST_CHAT_STORAGE_KEY, JSON.stringify(chatData));
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        context: 'saveGuestChatToStorage',
        messageCount: messages?.length || 0,
        error: err?.message
      }
    });
    console.warn('Failed to save guest chat to localStorage:', err);
  }
}

/**
 * Load guest chat messages from localStorage
 * @returns {Array<ChatMessage>|null} - Array of messages or null if not found/expired
 */
function loadGuestChatFromStorage() {
  try {
    const stored = localStorage.getItem(GUEST_CHAT_STORAGE_KEY);
    if (!stored) return null;

    const chatData = JSON.parse(stored);

    // Check if expired (24 hours)
    if (Date.now() - chatData.timestamp > CHAT_EXPIRY_HOURS * 60 * 60 * 1000) {
      clearGuestChatStorage();
      return null;
    }

    return chatData.messages;
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        context: 'loadGuestChatFromStorage',
        error: err?.message
      }
    });
    console.warn('Failed to load guest chat from localStorage:', err);
    return null;
  }
}

/**
 * Clear guest chat from localStorage
 */
function clearGuestChatStorage() {
  try {
    localStorage.removeItem(GUEST_CHAT_STORAGE_KEY);
    store.set(messagesAtom, []);
  } catch (err) {
    Sentry.captureException(err, {
      extra: {
        context: 'clearGuestChatStorage',
        error: err?.message
      }
    });
  }
}

// Agent event schemas for unauthenticated users
const RunStartedEventSchema = z.object({
  event: z.literal('RunStarted'),
  timestamp: z.string()
});

const RunCompletedEventSchema = z.object({
  event: z.literal('RunCompleted'),
  timestamp: z.string()
});

const RunResponseContentEventSchema = z.object({
  event: z.literal('RunResponseContent'),
  content: z.string(),
  timestamp: z.string()
});

const ToolCallStartedEventSchema = z.object({
  event: z.literal('ToolCallStarted'),
  tool_name: z.string(),
  args: z.union([z.string(), z.object({}).passthrough()]),
  timestamp: z.string()
});

const ToolCallCompletedEventSchema = z.object({
  event: z.literal('ToolCallCompleted'),
  tool_name: z.string(),
  tool_result: z.union([z.string(), z.object({}).passthrough()]),
  timestamp: z.string()
});

const StreamCompleteEventSchema = z.object({
  event: z.literal('stream_complete'),
  timestamp: z.string()
});

const ErrorEventSchema = z.object({
  event: z.literal('error'),
  error: z.string(),
  details: z.string().optional(),
  timestamp: z.string()
});

const ImageDataEventSchema = z.object({
  event: z.literal('image_data'),
  imageDataURL: z.string(),
  timestamp: z.string()
});

const AgentEventSchema = z.union([
  RunStartedEventSchema,
  RunCompletedEventSchema,
  RunResponseContentEventSchema,
  ToolCallStartedEventSchema,
  ToolCallCompletedEventSchema,
  StreamCompleteEventSchema,
  ErrorEventSchema,
  ImageDataEventSchema
]);

const AgentModeSchema = z.enum(Object.values(AgentMode))
const ActiveStreamRecordSchema = z.object({
  type: z.literal("chat"),
  query: z.object({
    message: z.string().optional(),
    files: z.array(z.object({}).passthrough()).optional(),
    images: z.array(z.object({}).passthrough()).optional(),
    mode: AgentModeSchema.optional(),
  }),
  response: z.nullable(
    z.union([
      z.object({
        type: z.literal("content"),
        content: z.string(),
      }),
      z.object({
        type: z.literal("structured-content"),
        content: z.array(ChatMessagePartSchema),
      }),
    ])
  ),
  timestamp: z.string(),
})

/**
 * Stream agent query for unauthenticated users
 * @param {Object} body
 * @param {string} body.query
 * @param {string} body.mode
 * @param {Object} [body.chatHistory]
 * @param {string} [body.image]
 */
async function streamAgentQuery(body) {
  // Check client-side rate limit
  try {
    checkClientRateLimit();
  } catch (err) {
    Sentry.captureException(err, {
      extra: { 
        context: 'guest-chat-provider - client rate limit check',
        body: body ? 'present' : 'missing'
      }
    });
    throw new RateLimitError(err.message, getRateLimitData()?.resetTime);
  }

  const baseURL = import.meta.env.VITE_SOCKET_URL;
  
  const response = await fetch(new URL('llm/agent', baseURL), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  if (response.status === 429) {
    let data;
    try {
      data = await response.json();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { 
          context: 'guest-chat-provider - parse rate limit response',
          responseStatus: response.status,
          responseStatusText: response.statusText
        }
      });
      data = { message: 'Rate limit exceeded', resetTime: null };
    }
    throw new RateLimitError(data.message, data.resetTime);
  }

  if (!response.ok) {
    throw new Error(`Agent request failed: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('No response body for streaming');
  }

  return response.body.getReader();
}

/**
 * Parse SSE data from agent stream
 * @param {string} chunk
 * @returns {Object|null}
 */
function parseSSEChunk(chunk) {
  const lines = chunk.split('\n');
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      try {
        const data = line.slice(6); // Remove 'data: ' prefix
        if (data.trim() === '') continue;
        return JSON.parse(data);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { 
            context: 'guest-chat-provider - parse SSE chunk',
            line: line?.substring(0, 100),
            chunkLength: chunk?.length
          }
        });
        console.warn('Failed to parse SSE data:', line, error);
      }
    }
  }
  return null;
}

/** @param {File} file */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/**
 * Uploads a file (txt, pdf, doc etc.) and returns its textual content.
 * Guest implementation is purely client-side.
 * @param {File} file
 * @returns {Promise<string>}
 */
async function uploadFile(file) {
  if (!file) throw new Error("No file provided");
  // For guests we only need the text content; binary parsing handled elsewhere if needed.
  return /** @type {string} */ (await readFileAsText(file));
}

/** @param {*} error */
function getErrorMessage(error) {
  return error?.message || error?.error || error?.description || error?.text || JSON.stringify(error) || '';
}

/** @param {*} error */
function getErrorStatus(error) {
  return error?.status || error?.statusCode || error?.code || 0;
}

/** @param {any} error */
function isRateLimitError(error) {
  const errorMessage = getErrorMessage(error);
  const errorStatus = getErrorStatus(error);

  // Enhanced rate limit error detection
  return errorMessage.toLowerCase().includes('rate limit') ||
    errorMessage.toLowerCase().includes('too many requests') ||
    errorMessage.toLowerCase().includes('maximum of') ||
    errorMessage.toLowerCase().includes('try again in') ||
    errorMessage.toLowerCase().includes('requests per') ||
    errorStatus === 429;
}

function getRateLimitStatus() {
  const data = getRateLimitData();
  if (!data) return { remaining: 5, resetTime: null };

  const now = Date.now();
  if (now > data.resetTime) {
    return { remaining: 5, resetTime: null };
  }

  return {
    remaining: Math.max(0, 5 - data.count),
    resetTime: data.resetTime
  };
}

/** @param {string} message */
const parseRateLimitMessage = (message) => {
  const maxRequestsMatch = message.match(/maximum of (\d+) requests per (\d+) (hours?|minutes?|days?)/i);
  const tryAgainMatch = message.match(/try again in (\d+) (hours?|minutes?|days?)/i);

  let maxRequests = maxRequestsMatch ? parseInt(maxRequestsMatch[1]) : 5;
  let waitTime = null;
  let waitUnit = null;

  if (tryAgainMatch) {
    waitTime = parseInt(tryAgainMatch[1]);
    waitUnit = tryAgainMatch[2];
  }

  return { maxRequests, waitTime, waitUnit };
};

/** @param {ChatMessage[] | Record<string, ChatMessage> | null} val */
function getMessagesArray(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'object') return Object.values(val);
  return [];
}

const GUEST_STUB_QUERY_KEY = ['guest-stub']
/**
 * @template T
 * @param {Partial<Parameters<typeof queryOptions<T, Error, any, any>>[0]>} [extraOptions]
 */
function getStubQueryOptions(extraOptions) {
  return queryOptions({
    queryFn: () => /**@type{T}*/(null),
    enabled: false,
    ...extraOptions,
    queryKey: [...GUEST_STUB_QUERY_KEY, ...(extraOptions?.queryKey || [])],
  })
}

/**
 * @template T
 * @param {Partial<Parameters<typeof infiniteQueryOptions<T, Error, any, any>>[0]>} [extraOptions]
 */
function getStubInfiniteQueryOptions(extraOptions) {
  return infiniteQueryOptions({
    queryFn: () => /**@type{T}*/(null),
    initialPageParam: null,
    getNextPageParam: () => /**@type{any}*/(null),
    enabled: false,
    ...extraOptions,
    queryKey: [...GUEST_STUB_QUERY_KEY, ...(extraOptions?.queryKey || [])],
  })
}

/**
 * @template T
 * @param {Partial<Parameters<typeof mutationOptions<T, Error, any, any>>[0]>} [extraOptions]
 */
function getStubMutationOptions(extraOptions) {
  return mutationOptions({
    mutationFn: () => null,
    ...extraOptions,
  })
}

const inputMessageAtom = atom({
  text: "",
  /** @type {ChatFile[]} */
  files: [],
  /** @type {ChatImage[]} */
  images: [],
})

const messagesAtom = atom(/** @type {ChatMessage[]} */(loadGuestChatFromStorage() || []))

const activeStreamAtom = atom(/**@type{z.infer<typeof ActiveStreamRecordSchema> | null}*/(null))

const agentModeAtom = atom(/**@type{import("./utils.js").AgentModeValue}*/(AgentMode.WEB_SEARCH))

/** @param {Partial<typeof inputMessageAtom extends import("jotai").PrimitiveAtom<infer T>? T: never>} message */
function revertSentMessage(message) {
  store.set(inputMessageAtom, prev => ({
    text: message.text || prev.text,
    files: message.files?.length > 0 ? message.files : prev.files,
    images: message.images?.length > 0 ? message.images : prev.images,
  }))
}

/**
 * @param {Error} error
 * @param {Object} options
 * @param {() => void} options.gotoLogin
 */
function showErrorToast(error, options) {
  if(error instanceof RateLimitError) {
    // TODO: Have live countdown in toast
    showRateLimitToast(error, options.gotoLogin)
  } else {
    toast.error(error.message)
  }
}

/**
 * @param {Error} error
 * @param {() => void} gotoLogin
 */
function showRateLimitToast(error, gotoLogin) {
  const errorMessage = getErrorMessage(error);
  const { maxRequests, waitTime, waitUnit } = parseRateLimitMessage(errorMessage);
  const waitTimeText = waitTime && waitUnit ? `${waitTime} ${waitUnit}` : 'a while';
  const rateLimitData = getRateLimitData();

  const timeLeft = (() => {
    const now = Date.now();
    const timeUntilReset = Math.max(0, rateLimitData.resetTime - now);

    const hours = Math.floor(timeUntilReset / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilReset % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntilReset % (1000 * 60)) / 1000);

    let timeDisplay = '';
    if (hours > 0) timeDisplay += `${hours}h `;
    if (minutes > 0) timeDisplay += `${minutes}m `;
    if (hours === 0) timeDisplay += `${seconds}s`;

    return timeDisplay.trim();
  })()
  const timeToReset = timeLeft || `in ${waitTimeText}`;

  toast.error("Rate Limit Exceeded", {
    duration: 1000 * 15, // 15 seconds
    description: `You've reached your request limit of ${maxRequests}. Please try again later. Next request available ${timeLeft ? 'in ' : ''} ${waitTimeText}`,
    action: {
      label: "Login for more access",
      onClick: () => {
        gotoLogin()
      },
    }
  })
}

const GuestMessagesEffectSymbol = Symbol('GuestMessagesEffect')

/** @satisfies {import("./index.jsx").ChatContextValues} */
const GuestChatContextValues = {
  useActiveChatSessionId() {
    return [null, () => {}]
  },
  useChatSessionList() {
    const id = useId()
    return useInfiniteQuery(getStubInfiniteQueryOptions({
      queryKey: [id],
      initialData: { pages: [], pageParams: [] },
    }))
  },
  useActiveChatSession() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
    }))
  },
  useCreateChatSession() {
    return useMutation(getStubMutationOptions())
  },
  useUpdateChatSession() {
    return useMutation(getStubMutationOptions())
  },
  useDeleteChatSession() {
    return useMutation(getStubMutationOptions())
  },

  useActiveProjectId() {
    return [null, () => {}]
  },
  useChatProjects() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
      initialData: [],
    }))
  },
  useCreateChatProject() {
    return useMutation(getStubMutationOptions())
  },

  useAgentMode() {
    return useAtom(agentModeAtom)
  },
  useModelConfiguration() {
    return [{temperature: null, systemPrompt: null, topP: null}, () => {}]
  },
  useInputMessage() {
    return useAtom(inputMessageAtom)
  },
  useSendChatMessage() {
    const [messages, setMessages] = useAtom(messagesAtom)
    const setActiveStream = useSetAtom(activeStreamAtom)
    const { authenticate } = useChatAuthContext()

    return useMutation({
      mutationFn: async (variables) => {
        // Resolve all image DataURLs before constructing the outgoing message
        const resolvedImages = await Promise.all(
          (variables?.images ?? []).map(img => {
            if (img.resourceUrl) return Promise.resolve(img.resourceUrl);
            if (img.localFile instanceof File) {
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(/** @type {string} */(reader.result));
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(img.localFile);
              });
            }
            return Promise.resolve(null);
          })
        ).then(urls => urls.filter(Boolean));

        // Compute the new messages array using { user, content } structure
        const safeMessages = getMessagesArray(messages);
        const now = new Date().toISOString();
        /** @type {ChatMessage} */
        const newUserMessage = {
          user: 'user',
          text: variables.text,
          timestamp: now,
        };

        // Attach file/image metadata when provided
        if (resolvedImages.length > 0) {
          newUserMessage.images = resolvedImages;
        }
        const fileContents = (variables?.files ?? []).map(f => f.content).filter(Boolean);
        if (fileContents.length > 0) {
          newUserMessage.files = fileContents;
        }

        const newMessages = [...safeMessages, newUserMessage]

        try {
          const reader = await streamLLMInference({
            chatHistory: { chat: newMessages },
            country: 'Ghana',
            role: 'general_public',
            temperature: 0.7,
            topP: 0.9,
            mode: 'inference',
          });

          const decoder = new TextDecoder();

          while (true) {
            const { value, done } = await reader.read();
            if(done) break
            if(!value) continue

            const chunkStr = decoder.decode(value, { stream: true });

            if(chunkStr.trim().startsWith("{")) {
              try {
                const obj = JSON.parse(chunkStr.trim());
                if (obj.imageDataURL && resolvedImages.length === 0) continue
              } catch(error) {
                // Not image json - treat as normal chunk
              }
            }

            setActiveStream(prev => ({
              ...prev,
              response: {
                ...prev.response,
                type: "content",
                content: prev.response.content + chunkStr,
              },
            }))
          }

          const activeStream = store.get(activeStreamAtom)
          /** @satisfies {ChatMessage[]} */
          const messages = [
            newUserMessage,
            {
              user: AI_USER,
              text: activeStream.response.content,
              timestamp: new Date().toISOString(),
            }
          ]

          return {
            sessionId: "",
            messages,
            /** @type {ChatImage[]} */
            images: [],
            /** @type {ChatFile[]} */
            files: [],
            /** @type {ChatReport[]} */
            reports: [],
          }
        } catch(err) {
          console.log('GuestChatProvider sendMessage error', err);
          if (err instanceof RateLimitError) {
            throw err
          } else {
            throw new Error('Failed to get response');
          }
        }
      },
      onMutate(variables) {
        const previewImages = (variables.images ?? []).map(img => ({
          ...img,
          previewUrl: img.localFile instanceof File ? URL.createObjectURL(img.localFile) : undefined,
        }))
        setActiveStream({
          type: "chat",
          query: {
            message: variables.text,
            files: variables.files,
            images: previewImages,
          },
          response: {
            type: "content",
            content: "",
          },
          timestamp: new Date().toISOString(),
        })
      },
      onSuccess(data, variables) {
        setMessages(prev => [...prev, ...data.messages])
      },
      onError(error, variables) {
        showErrorToast(error, {
          gotoLogin: () => {
            authenticate().catch(console.error)
          }
        })

        revertSentMessage({
          text: variables.text,
          files: variables.files,
          images: variables.images,
        })
      },
      onSettled() {
        setActiveStream(null)
      }
    })
  },
  useEditChatMessage() {
    return useMutation(getStubMutationOptions())
  },
  useRegenerateChatMessage() {
    return useMutation(getStubMutationOptions())
  },
  useSendAgentMessage() {
    const { authenticate } = useChatAuthContext()
    const [messages, setMessages] = useAtom(messagesAtom)
    const mode = useAtomValue(agentModeAtom)
    const setActiveStream = useSetAtom(activeStreamAtom)

    /** @param {*} content */
    function appendStreamResponseContent(content) {
      setActiveStream(stream => {
        if (stream.response != null && stream.response.type !== "structured-content") return stream
        return {
          ...stream,
          response: {
            ...stream.response,
            type: "structured-content",
            content: [...(stream.response?.content ?? []), content]
          }
        }
      })
    }

    return useMutation({
      mutationFn: async (variables) => {
        if (mode !== AgentMode.WEB_SEARCH) {
          throw new Error('Only web_search mode is supported for unauthenticated users');
        }

        // Resolve all image DataURLs before constructing the outgoing message
        const resolvedImages = await Promise.all(
          (variables?.images ?? []).map(img => {
            if (img.resourceUrl) return Promise.resolve(img.resourceUrl);
            if (img.localFile instanceof File) {
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(/** @type {string} */(reader.result));
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(img.localFile);
              });
            }
            return Promise.resolve(null);
          })
        ).then(urls => urls.filter(Boolean));

        // Compute the new messages array using { user, content } structure
        const safeMessages = getMessagesArray(messages);
        const now = new Date().toISOString();
        /** @type {ChatMessage} */
        const newUserMessage = {
          user: 'user',
          text: variables.query,
          timestamp: now,
        };

        // Attach image metadata when provided
        if (resolvedImages.length > 0) {
          newUserMessage.images = resolvedImages;
        }

        const newMessages = [...safeMessages, newUserMessage]

        try {
          // Prepare chat history for agent
          const chatHistory = {
            chat: newMessages.map(msg => ({
              user: msg.user === 'user' ? 'user' : 'agent',
              text: msg.text || '',
              ...(msg.images?.length && { images: msg.images }),
              timestamp: msg.timestamp
            }))
          };

          const reader = await streamAgentQuery({
            query: variables.query,
            mode: variables.mode,
            chatHistory,
          });

          const decoder = new TextDecoder();
          let done = false;
          let buffer = '';

          while (!done) {
            const { value, done: streamDone } = await reader.read();
            done = streamDone;

            if (value) {
              buffer += decoder.decode(value, { stream: true });

              // Process complete SSE messages
              const lines = buffer.split('\n\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.trim() === '') continue;

                const eventData = parseSSEChunk(line);
                if (!eventData) continue;

                try {
                  const event = AgentEventSchema.parse(eventData);

                  switch (event.event) {
                    case 'image_data':
                      break;

                    case 'RunStarted':
                      break;

                    case 'RunResponseContent':
                      appendStreamResponseContent({
                        event: 'RunResponseContent',
                        data: event
                      })
                      break;

                    case 'ToolCallStarted':
                      appendStreamResponseContent({
                        event: 'ToolCallStarted',
                        data: event
                      });
                      break;

                    case 'ToolCallCompleted':
                      appendStreamResponseContent({
                        event: 'ToolCallCompleted',
                        data: event
                      });
                      break;

                    case 'stream_complete':
                      break;

                    case 'error':
                      throw new Error(event.error);

                    default:
                      console.warn('Unknown agent event type:', event.event);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse agent event:', eventData, parseError);
                }
              }
            }
          }

          const activeStream = store.get(activeStreamAtom)
          /** @satisfies {ChatMessage[]} */
          const messages = [
            newUserMessage,
            {
              user: AGENT_USER,
              text: "",
              message: activeStream.response.content,
              timestamp: new Date().toISOString(),
            }
          ]

          return {
            sessionId: "",
            messages,
            /** @type {ChatImage[]} */
            images: [],
            /** @type {ChatFile[]} */
            files: [],
            /** @type {ChatReport[]} */
            reports: [],
          }
        } catch (err) {
          console.log('GuestChatProvider sendAgentMessage error', err);
          if (err instanceof RateLimitError) {
            throw err
          } else {
            throw new Error(err.message || 'Failed to get agent response');
          }
        } 
      },
      onMutate(variables) {
        const previewImages = (variables.images ?? []).map(img => ({
          ...img,
          previewUrl: img.localFile instanceof File ? URL.createObjectURL(img.localFile) : undefined,
        }))
        setActiveStream({
          type: "chat",
          query: {
            message: variables.query,
            files: [],
            images: previewImages,
          },
          response: {
            type: "structured-content",
            content: [],
          },
          timestamp: new Date().toISOString(),
        })
      },
      onSuccess(data, variables) {
        setMessages(prev => [...prev, ...data.messages])
      },
      onError(error, variables) {
        showErrorToast(error, {
          gotoLogin: () => {
            authenticate().catch(console.error)
          }
        })

        revertSentMessage({
          text: variables.query,
          files: [],
          images: variables.images,
        })
      },
      onSettled() {
        setActiveStream(null)
      }
    })
  },
  useSendChatFeedback() {
    return useMutation(getStubMutationOptions())
  },
  useActiveChatMessages() {
    const id = useId()
    return useInfiniteQuery(getStubInfiniteQueryOptions({
      queryKey: [id],
      initialData: { pages: [], pageParams: [] },
      select: (data) => data.pages.flatMap(page => page.messages ?? []),
    }))
  },
  useUploadFile() {
    return useMutation({
      mutationFn: (variables) => uploadFile(variables.file),
    })
  },

  useActiveChatSessionReports() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
      initialData: [],
    }))
  },
  useGenerateReport() {
    return useMutation(getStubMutationOptions())
  },
  useUpdateReport() {
    return useMutation(getStubMutationOptions())
  },

  useActiveChatSessionImages() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
      initialData: [],
    }))
  },
  useChatSessionThumbnailImage() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
    }))
  },
  useUploadChatSessionImage() {
    return useMutation(getStubMutationOptions())
  },

  useStreamingMessage() {
    const activeStream = useAtomValue(activeStreamAtom);

    if (!activeStream) {
      return { query: null, response: null };
    }

    const queryMessage = activeStream.query?.message ? {
      user: 'user',
      text: activeStream.query.message,
      timestamp: activeStream.timestamp,
      images: activeStream.query.images,
      files: activeStream.query.files,
    } : null;

    let responseMessage = null;

    if (activeStream.response) {
      if (activeStream.response.type === "content") {
        responseMessage = {
          user: AI_USER,
          text: activeStream.response.content || '',
          timestamp: activeStream.timestamp,
        };
      } else if (activeStream.response.type === "structured-content") {
        const responseUser = (() => {
          if (activeStream.query.mode === AgentMode.AGENT) return AGENT_USER;
          if (activeStream.query.mode === AgentMode.DEEP_RESEARCH) return DEEP_RESEARCH_USER;
          return AGENT_USER;
        })();

        responseMessage = {
          user: responseUser,
          text: '',
          message: activeStream.response.content || [],
          timestamp: activeStream.timestamp,
        };
      }
    }

    return { query: queryMessage, response: responseMessage };
  },
  useStreamingReport() {
    return null;
  },
  useActiveSessionHasActiveStream() {
    const [hasStreamAtom] = useState(() => (
      atom(get => get(activeStreamAtom) != null)
    ))

    const hasStream = useAtomValue(hasStreamAtom)
    return hasStream
  },
  useGetChatSessionStream() {
    return useMutation(getStubMutationOptions())
  },
  useCancelActiveChatStream() {
    return useMutation(getStubMutationOptions())
  },

  useGeolocation() {
    const id = useId()
    return useQuery(getStubQueryOptions({
      queryKey: [id],
    }))
  },
  useIsGuestMode() {
    return true
  },
  useGuestMessages() {
    const messages = useAtomValue(messagesAtom)

    useSharedEffect(GuestMessagesEffectSymbol, () => {
      if (messages.length > 0) {
        saveGuestChatToStorage(messages);
      }
    }, [messages]);

    return messages
  },
}

/**
 * @param {Object} props 
 * @param {React.ReactNode} props.children
 */
function GuestChatProvider({ children }) {
  const queryClient = useQueryClient()

  const { user } = useSelector(state => state.app.datastate)
  const isLoggedIn = user?._id != null
  useEffect(() => {
    if(isLoggedIn) return
    return () => queryClient.removeQueries({ queryKey: GUEST_STUB_QUERY_KEY })
  }, [isLoggedIn, queryClient])

  return (
    <ChatContext.Provider value={GuestChatContextValues}>
      {children}
    </ChatContext.Provider>
  )
}

export default GuestChatProvider
export { loadGuestChatFromStorage, clearGuestChatStorage }
